import type { BalanceBundle, Guild, TelegramUser } from '@guildrise/shared';
import { useState, useSyncExternalStore } from 'react';
import { getAuthRevision, subscribeAuthSession } from '../../shared/api.js';
import { hapticSelect } from '../../shared/haptics.js';
import { GuildAvatar } from '../../shared/ui/guild-avatar.js';
import { ArenaEntry } from '../arena/arena-entry.js';
import { CollectionScreen } from '../collection/CollectionScreen.js';
import { ProfileScreen } from '../profile/ProfileScreen.js';

const TABS = [
  { id: 'arsenal', label: 'Арсенал', art: 'arsenal' },
  { id: 'adventures', label: 'Приключения', art: 'adventures' },
  { id: 'hire', label: 'Найм', art: 'hire' },
  { id: 'alliances', label: 'Гильдия', art: 'alliances' },
  { id: 'profile', label: 'Профиль', art: 'profile' },
] as const;
type TabId = (typeof TABS)[number]['id'];

interface Props {
  readonly guild: Guild;
  readonly player: TelegramUser;
  readonly frameArt?: string | undefined;
  readonly balance: BalanceBundle;
}

export function GameShell({ guild, player, frameArt, balance }: Props): React.ReactElement {
  const [tab, setTab] = useState<TabId>('hire');
  const authRevision = useSyncExternalStore(subscribeAuthSession, getAuthRevision, getAuthRevision);

  return (
    <div
      className={`shell${tab === 'profile' ? ' shell--profile' : ''}${tab === 'adventures' ? ' shell--arena' : ''}`}
    >
      {tab !== 'profile' && (
        <header className="shell__top">
          <div className="shell__guild">
            <GuildAvatar
              frameId={guild.frameId}
              frameArt={frameArt}
              name={guild.name}
              logoUrl={guild.logoUrl ?? undefined}
              size={40}
            />
            <div className="shell__who">
              <span className="shell__name">
                {guild.name}
                <span className="shell__tag tabular">#{guild.discriminator}</span>
              </span>
            </div>
          </div>
        </header>
      )}

      <main className={`shell__body${tab === 'profile' ? ' shell__body--profile' : ''}`} key={tab}>
        {tab === 'arsenal' ? (
          <CollectionScreen
            key={`${guild.id}:${authRevision}`}
            guildId={guild.id}
            catalog={balance.characters}
          />
        ) : tab === 'adventures' ? (
          <ArenaEntry
            key={`${guild.id}:${authRevision}`}
            catalog={balance.characters}
            guild={guild}
            frameArt={frameArt}
          />
        ) : tab === 'profile' ? (
          <ProfileScreen guild={guild} player={player} frameArt={frameArt} balance={balance} />
        ) : (
          <section className="soon enter">
            <h1 className="h2">{tab === 'hire' ? 'Найм' : 'Гильдия'}</h1>
            <p className="body">
              {tab === 'hire'
                ? 'Контракты и сундуки появятся позже.'
                : 'Функции гильдии появятся позже.'}
            </p>
          </section>
        )}
      </main>

      <nav className="tabs" aria-label="Разделы">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`tabs__item${item.id === tab ? ' tabs__item--on' : ''}`}
            aria-current={item.id === tab ? 'page' : undefined}
            onClick={() => {
              if (item.id === tab) return;
              hapticSelect();
              window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
              setTab(item.id);
            }}
          >
            <span className="tabs__icon">
              <img
                className="tabs__art"
                src={`/art/warm-ranks/nav-${item.art}-${item.id === 'hire' ? 'v7' : 'v6'}.webp`}
                width={32}
                height={32}
                alt=""
                draggable={false}
              />
            </span>
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
